/**
 * The customer's own unit, read back off a persisted enquiry line (point 12).
 *
 * The full reasoning is in this module's test. In short: when the UOM master cannot verify what
 * the customer wrote, `toCreateLine` forces the line to the `Nos` placeholder and folds the
 * customer's actual words into `raw_text` as "DESCRIPTION (customer's unit: DRUMS)". That is the
 * only record of what was asked for — and it is exactly the case where the purchaser has to make
 * a decision.
 *
 * The RFQ row used to show this from the in-memory extraction result, so it appeared while the
 * enquiry was being created and was gone the next time anyone opened it. These read it off the
 * line itself, so it survives a reload.
 *
 * ---- Deliberately a READ, not a write ----
 *
 * Nothing here offers to apply the customer's unit. By construction it is a value the UOM master
 * rejected, so writing it would fail — see `LineSuggestion.customerUom`'s own note. The purchaser
 * picks a real unit from the item's conversions; this just makes sure they can see what they are
 * translating FROM while they do it.
 */

/**
 * Matches the suffix `toCreateLine` writes, with either apostrophe and any capitalisation, and
 * only at the END of the text where it is actually written. A description that happens to contain
 * the phrase mid-sentence is the customer's own words, not our annotation.
 */
const ANNOTATION = /\(customer['’]s unit:\s*([^)]*)\)\s*$/i

export function customerUomOf(rawText: string | null | undefined): string | null {
  const match = ANNOTATION.exec(rawText ?? '')
  if (!match) return null
  // Preserved as the customer wrote it — only the padding introduced by the annotation is removed.
  const uom = match[1].trim()
  return uom === '' ? null : uom
}



/* ==============================================================================================
 * The second source: the customer's own text
 *
 * The annotation above only exists where the UOM master REJECTED what the customer wrote — 4 lines
 * in 79 on a real provision enquiry. On the other 75 the customer's unit is sitting in their own
 * words ("RICE BASMATI-35 KG BAG" on a line the extraction put on KG), and showing nothing there
 * was the whole complaint.
 *
 * So a weaker second source, labelled as weaker. See the test for the measurement and for the
 * known false positive it cannot avoid.
 * ============================================================================================ */

/**
 * Packaging units, as a customer writes them. Deliberately a copy of `packSize.ts`'s `COUNT_UNITS`
 * rather than an import: that set exists to decide pack-size ARITHMETIC, and a unit belongs there
 * for reasons that have nothing to do with whether a customer would end a line with it. Tying the
 * two together would make a change to either silently alter the other.
 */
const PACKAGING_UNITS = new Set([
  'pkt', 'packet', 'pack', 'nos', 'no', 'pc', 'piece', 'ea', 'each', 'unit',
  'box', 'ctn', 'carton', 'case', 'doz', 'dozen', 'pr', 'pair', 'bag', 'roll',
  'tin', 'can', 'btl', 'botl', 'bottle', 'jar', 'tube', 'set', 'coil', 'drum', 'pail',
])

/**
 * `CASES` and `CASE` are one unit. The live data has a line on `CASE` annotated `cases`, and
 * treating those as a disagreement would put a warning on a line that is already right.
 *
 * Naive and intentionally so: trailing `s` only. `boxes`→`boxe` does not resolve to `box`, which
 * is why the plural forms that matter are compared against the set both ways.
 */
const singular = (unit: string): string => {
  const lower = unit.trim().toLowerCase()
  return lower.endsWith('es') && PACKAGING_UNITS.has(lower.slice(0, -2))
    ? lower.slice(0, -2)
    : lower.endsWith('s') && PACKAGING_UNITS.has(lower.slice(0, -1))
      ? lower.slice(0, -1)
      : lower
}

/**
 * Abbreviations of the SAME WORD, folded to one form.
 *
 * Found by rendering the hint across all 79 lines of a real enquiry: a line on `BTL` carrying the
 * recorded unit `BOTL` was warned about. Both are "bottle", so the purchaser was being sent to fix
 * something already correct — and a warning that is wrong once is a warning nobody reads twice.
 *
 * ONLY same-word abbreviations. `Nos` and `PC` are not merged however interchangeable they look:
 * that is a judgement about the business, not about spelling, and it is not this file's to make.
 */
const ABBREVIATIONS: Record<string, string> = {
  botl: 'bottle', btl: 'bottle', bottle: 'bottle',
  ctn: 'carton', carton: 'carton',
  pkt: 'packet', packet: 'packet',
  pc: 'piece', piece: 'piece',
  doz: 'dozen', dozen: 'dozen',
  pr: 'pair', pair: 'pair',
}

const canonical = (unit: string): string => {
  const base = singular(unit)
  return ABBREVIATIONS[base] ?? base
}

/** Whether two units mean the same thing, ignoring case, padding, plural and abbreviation. */
export const sameUnit = (a: string, b: string): boolean => canonical(a) === canonical(b)

/**
 * The packaging word the customer's text ENDS on, if any.
 *
 * End position only. "BOX CUTTER" and "CAN OPENER" are products; an ordering unit is written last,
 * which is what "35 KG BAG" and "-20 KG PKT" both do. It cannot tell those from "TEA- BAG", where
 * BAG is part of the product name — see the test. That limit is why the result is labelled
 * `inferred` and never drives anything but a line of text.
 */
function inferredUnit(rawText: string): string | null {
  const words = withoutAnnotation(rawText).match(/[A-Za-z]+/g)
  if (!words || words.length === 0) return null
  const last = words[words.length - 1]
  return PACKAGING_UNITS.has(singular(last)) ? last : null
}

const withoutAnnotation = (text: string): string => text.replace(ANNOTATION, '').trim()

export interface CustomerUomHint {
  /** As the customer wrote it. */
  uom: string
  /**
   * `recorded` — the extraction captured this exact unit and the master rejected it. A fact.
   * `inferred` — read off the end of the customer's own text. An observation, and sometimes wrong.
   */
  source: 'recorded' | 'inferred'
  /** Whether the line is currently on a different unit. Plural- and case-insensitive. */
  differs: boolean
}

/**
 * What the customer asked in, from the best evidence available — the recorded annotation first,
 * the text second, nothing if neither has anything.
 */
export function customerUomHint(rawText: string | null | undefined, lineUom: string, storedCustomerUom?: string | null): CustomerUomHint | null {
  const text = rawText ?? ''

  // The line's own `customer_uom` field, where it is stored (a Link again since 2026-09-17) —
  // the record, ahead of the annotation the create path wrote before the field existed.
  const stored = (storedCustomerUom ?? '').trim()
  if (stored !== '') return { uom: stored, source: 'recorded', differs: !sameUnit(stored, lineUom) }

  const recorded = customerUomOf(text)
  if (recorded !== null) {
    return { uom: recorded, source: 'recorded', differs: !sameUnit(recorded, lineUom) }
  }

  const inferred = inferredUnit(text)
  if (inferred === null) return null
  return { uom: inferred, source: 'inferred', differs: !sameUnit(inferred, lineUom) }
}
